// =============================================================================
// App.tsx — Roteador raiz e providers de infraestrutura.
//
// Estrutura de rotas:
//   /login          → público
//   /primeiro-acesso → autenticado + pendente, fora do AppLayout (F56-S02)
//   /               → protegido (AuthGuard > AppLayout > DashboardPage)
//   /tarefas        → protegido (F15-S10: painel minhas tarefas)
//   /notificacoes   → protegido (F26-S04: central de notificações)
//   /conversas      → protegido (F16-S15: inbox live chat — UI em S16/S17)
//   /leads etc.     → protegido (placeholder)
//   *               → redireciona /login
//
// QueryClient: staleTime 30s, sem refetchOnWindowFocus, retry 1x.
//
// Realtime (F27-S07, doc 24 §5.4): `SocketProvider` é montado UMA ÚNICA VEZ,
// envolvendo `<AppLayout />` dentro do `AuthGuard` (token já disponível) — o
// namespace /livechat fica conectado em TODAS as rotas autenticadas, não só
// em /conversas. O `Route` layout (AuthGuard > SocketProvider > AppLayout)
// não remonta ao navegar entre rotas filhas (react-router mantém o elemento
// da rota-pai montado), então não há reconexão nem duplo-mount do socket —
// só existe este ponto de montagem em toda a árvore (grep `<SocketProvider`
// antes de adicionar outro). `ConversasPage` deixou de montar o seu próprio
// provider (F16-S15) — reusa este via `useSocket()`.
// =============================================================================

import { WHATSAPP_MONITORING_POLICY_KEY } from '@elemento/shared-schemas';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AppLayout } from './app/AppLayout';
import { AuthGuard } from './app/AuthGuard';
import { ADMIN_ACCESS, firstAccessibleHref, NAV_ACCESS } from './app/navigation';
import { RequirePermission } from './app/RequirePermission';
import { SessionBootstrap } from './app/SessionBootstrap';
import { ToastProvider } from './components/ui/Toast';
import { PersonalEmailModal } from './features/account/PersonalEmailModal';
import { usePersonalEmailGuard } from './features/account/usePersonalEmailGuard';
import { DocumentTypesPage } from './features/admin/document-types/DocumentTypesPage';
import { InboxAssignmentsPage } from './features/admin/inbox-assignments/InboxAssignmentsPage';
import { RolesPage } from './features/admin/roles/RolesPage';
import { AiActionsPage } from './features/ai-actions';
import { shouldMarkFirstAccessPending } from './features/auth/firstAccess';
import { FirstAccessPage } from './features/auth/FirstAccessPage';
import { LoginPage } from './features/auth/LoginPage';
import { CollectionJobsPage, CollectionRulesPage, PaymentDuesPage } from './features/billing';
import {
  ConversationTimelinePage,
  DecisionsListPage,
  PlaygroundPage,
  PromptDetailPage,
  PromptsListPage,
} from './features/configuracoes/ai-console';
import { VendedoresPage } from './features/configuracoes/canais/VendedoresPage';
import { ComercialSettingsPage } from './features/configuracoes/comercial';
import { ConfiguracoesPage } from './features/configuracoes/ConfiguracoesPage';
import { ContractsPage } from './features/contracts';
import { CreditAnalysesListPage, CreditAnalysisDetailPage } from './features/credit-analyses';
import { CrmDetailPage } from './features/crm/CrmDetailPage';
import { CrmListPage } from './features/crm/CrmListPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { FollowupJobsPage, FollowupRulesPage } from './features/followup';
import { DocPage } from './features/help/DocPage';
import { HelpHomePage } from './features/help/HelpHomePage';
import { ImportWizardPage } from './features/imports/ImportWizardPage';
import { LawFirmsPage } from './features/law-firms/LawFirmsPage';
import { MeuWhatsAppInboxPage } from './features/meu-whatsapp/MeuWhatsAppInboxPage';
import { MeuWhatsAppPage } from './features/meu-whatsapp/MeuWhatsAppPage';
import { PolicyAcceptancesPage } from './features/policies/admin/PolicyAcceptancesPage';
import { RelatoriosPage } from './features/relatorios/RelatoriosPage';
import { AgendaPage } from './features/sales/agenda';
import { CampaignsPage } from './features/sales/campaigns';
import { CampaignDetailPage } from './features/sales/campaigns/detail';
import { OperatePage } from './features/sales/campaigns/operate';
import { NewCampaignWizardPage } from './features/sales/campaigns/wizard';
import { TasksPage } from './features/tasks';
import { TemplateDetailPage, TemplateFormPage, TemplatesListPage } from './features/templates';
import { ApiError } from './lib/api';
import { useAuth, useAuthStore } from './lib/auth-store';
import { SocketProvider } from './lib/realtime/SocketProvider';
import { AgentsPage } from './pages/admin/Agents';
import { CanaisAdminPage } from './pages/admin/Canais';
import { CitiesPage } from './pages/admin/Cities';
import { FeatureFlagsPage } from './pages/admin/FeatureFlags';
import { NotificacoesPage } from './pages/admin/Notificacoes';
import { ProductDetailPage } from './pages/admin/ProductDetail';
import { ProductsPage } from './pages/admin/Products';
import { QuickRepliesPage } from './pages/admin/QuickReplies';
import { TutoriaisPage } from './pages/admin/Tutoriais';
import { UsersPage } from './pages/admin/Users';
import { ConversasPage } from './pages/ConversasPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { SimulatorPage } from './pages/simulator/SimulatorPage';

// Code-split: ApiReferencePage não está no main bundle — só carregado quando
// o usuário acessa /ajuda/api. Economiza ~15-20 KB gzipped no main chunk.
const ApiReferencePage = React.lazy(() =>
  import('./features/help/api-reference/ApiReferencePage').then((m) => ({
    default: m.ApiReferencePage,
  })),
);

/**
 * 403 CREDENTIALS_CHANGE_REQUIRED em QUALQUER chamada = o servidor diz que a troca de
 * e-mail/senha está pendente (ex.: administrador redefiniu a senha com a sessão aberta).
 * Marca o estado "pendente" e o AuthGuard leva o usuário a /primeiro-acesso (F56-S02).
 */
function syncFirstAccessFromError(error: unknown): void {
  if (shouldMarkFirstAccessPending(error, useAuthStore.getState().isAuthenticated)) {
    useAuthStore.getState().setFirstAccess('pending');
  }
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: syncFirstAccessFromError }),
  mutationCache: new MutationCache({ onError: syncFirstAccessFromError }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // Não retentar erros 4xx (rate-limit/permissão/validação) — retentar no
      // 429 amplificava o refetch storm. Só retenta falhas de rede/5xx (1x).
      retry: (failureCount, error) => {
        const status = error instanceof ApiError ? error.status : undefined;
        if (status !== undefined && status >= 400 && status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

export function App(): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          <SessionBootstrap>
            <AppRoutes />
          </SessionBootstrap>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}

/**
 * Guard de 1º login — exibe o modal bloqueante de email pessoal quando necessário.
 *
 * Renderizado DENTRO do AppRoutes para ter acesso ao QueryClient e ao
 * estado de autenticação. O modal sobrepõe toda a UI via position:fixed z-50,
 * então não importa qual rota está ativa — o agente não consegue interagir.
 *
 * F14-S04 D3: fluxo obrigatório antes de qualquer uso do sistema.
 */
function PersonalEmailGuard(): React.JSX.Element | null {
  const { requiresPersonalEmail, refetch } = usePersonalEmailGuard();

  if (!requiresPersonalEmail) return null;

  return (
    <PersonalEmailModal
      onSuccess={() => {
        // Revalida o perfil — após o cadastro, requiresPersonalEmail deve ser false
        refetch();
      }}
    />
  );
}

/**
 * Dashboard ("/"): destino pós-login. Quem não tem dashboard (cobrança, diretoria…)
 * não cai numa tela de "sem acesso": vai para a primeira tela que o papel abre.
 */
function DashboardRoute(): React.JSX.Element {
  const { hasPermission } = useAuth();
  return (
    <RequirePermission
      access={NAV_ACCESS.dashboard}
      redirectTo={firstAccessibleHref(hasPermission)}
    >
      <DashboardPage />
    </RequirePermission>
  );
}

function AppRoutes(): React.JSX.Element {
  // Só consulta o perfil quando o primeiro acesso está resolvido: enquanto pendente (ou
  // ainda desconhecido) o servidor responde 403 a /api/account/profile (F56-S02).
  const firstAccessDone = useAuthStore((s) => s.firstAccess === 'done');
  return (
    <>
      {/* Guard de 1º login — sobrepõe toda a UI quando necessário (F14-S04) */}
      {firstAccessDone && <PersonalEmailGuard />}
      <Routes>
        {/* ── Pública ────────────────────────────────────────────────────────── */}
        <Route path="/login" element={<LoginPage />} />

        {/* ── Primeiro acesso (F56-S02): autenticado e pendente, fora do AppLayout
            (sem menu nem barra). Não pendente é levado a "/". ─────────────────── */}
        <Route
          path="/primeiro-acesso"
          element={
            <AuthGuard surface="first-access">
              <FirstAccessPage />
            </AuthGuard>
          }
        />

        {/* ── Protegidas (AuthGuard > SocketProvider > AppLayout) ──────────── */}
        {/* SocketProvider global (F27-S07): único mount da árvore — ver nota de
            cabeçalho. Sino tem realtime em toda rota; fora, poll de 60s como fallback. */}
        <Route
          element={
            <AuthGuard>
              <SocketProvider>
                <AppLayout />
              </SocketProvider>
            </AuthGuard>
          }
        >
          <Route index element={<DashboardRoute />} />
          {/* Legacy redirects — bookmarks antigos preservados */}
          <Route path="/kanban" element={<Navigate to="/crm?view=kanban" replace />} />
          <Route path="/leads" element={<Navigate to="/crm" replace />} />
          <Route
            path="/crm"
            element={
              <RequirePermission access={NAV_ACCESS.crm}>
                <CrmListPage />
              </RequirePermission>
            }
          />
          <Route
            path="/crm/:id"
            element={
              <RequirePermission access={NAV_ACCESS.crm}>
                <CrmDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/imports/leads/new"
            element={
              <RequirePermission access={NAV_ACCESS.leadImport}>
                <ImportWizardPage />
              </RequirePermission>
            }
          />
          <Route
            path="/simulator"
            element={
              <RequirePermission access={NAV_ACCESS.simulator}>
                <SimulatorPage />
              </RequirePermission>
            }
          />
          {/* Legacy redirect — /analise era placeholder; a rota real é /credit-analyses (F4-S03) */}
          <Route path="/analise" element={<Navigate to="/credit-analyses" replace />} />
          {/* F4-S03: Análise de crédito */}
          <Route
            path="/credit-analyses"
            element={
              <RequirePermission access={NAV_ACCESS.creditAnalyses}>
                <CreditAnalysesListPage />
              </RequirePermission>
            }
          />
          <Route
            path="/credit-analyses/:id"
            element={
              <RequirePermission access={NAV_ACCESS.creditAnalyses}>
                <CreditAnalysisDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/contratos"
            element={
              <RequirePermission access={NAV_ACCESS.contracts}>
                <ContractsPage />
              </RequirePermission>
            }
          />
          {/* F33-S05: Agenda comercial — gate próprio de permissão + flag dentro da página */}
          <Route path="/agenda" element={<AgendaPage />} />
          {/* F35-S07: Campanhas comerciais — gate próprio de permissão + flag dentro da página */}
          <Route path="/campanhas" element={<CampaignsPage />} />
          {/* F35-S08: Wizard de criação — gate próprio de permissão + flag dentro da página.
              Precisa vir ANTES de qualquer /campanhas/:campaignId futuro (F35-S09) para "nova"
              nunca ser capturado como um id de campanha. */}
          <Route path="/campanhas/nova" element={<NewCampaignWizardPage />} />
          {/* F35-S10: Operação da campanha (um cliente por vez) — gate próprio de permissão + flag dentro da página */}
          <Route path="/campanhas/:campaignId/operar" element={<OperatePage />} />
          {/* F35-S09: Painel de detalhe da campanha — gate próprio de permissão + flag dentro da página */}
          <Route path="/campanhas/:campaignId" element={<CampaignDetailPage />} />
          <Route
            path="/relatorios"
            element={
              <RequirePermission access={NAV_ACCESS.reports}>
                <RelatoriosPage />
              </RequirePermission>
            }
          />
          <Route path="/configuracoes" element={<ConfiguracoesPage />} />
          <Route
            path="/configuracoes/advocacia"
            element={
              <RequirePermission access={ADMIN_ACCESS.lawFirms}>
                <LawFirmsPage />
              </RequirePermission>
            }
          />
          {/* F34-S06: Configuração comercial — gate próprio de permissão + flag dentro da página */}
          <Route path="/configuracoes/comercial" element={<ComercialSettingsPage />} />
          {/* F39-S09: pareamento do WhatsApp dos vendedores — gate por
              whatsapp_sync.pairing.enabled dentro da página (nasce desligada) */}
          <Route
            path="/configuracoes/canais/vendedores"
            element={
              <RequirePermission access={ADMIN_ACCESS.channels}>
                <VendedoresPage />
              </RequirePermission>
            }
          />
          {/* F39-S08: o vendedor conecta o proprio aparelho. URL enviada por
              link; o agente e derivado da sessao, nunca da URL. */}
          <Route path="/meu-whatsapp" element={<MeuWhatsAppPage />} />
          {/* F43-S13: caixa pessoal do vendedor — recorte de /conversas
              filtrado pelo canal WAHA dele. Gate por waha.inbox.enabled
              dentro da página (nasce desligada, ver cabeçalho do arquivo). */}
          <Route path="/meu-whatsapp/inbox" element={<MeuWhatsAppInboxPage />} />
          <Route
            path="/configuracoes/ia/prompts"
            element={
              <RequirePermission access={ADMIN_ACCESS.aiPrompts}>
                <PromptsListPage />
              </RequirePermission>
            }
          />
          <Route
            path="/configuracoes/ia/prompts/:key"
            element={
              <RequirePermission access={ADMIN_ACCESS.aiPrompts}>
                <PromptDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/configuracoes/ia/decisoes"
            element={
              <RequirePermission access={ADMIN_ACCESS.aiDecisions}>
                <DecisionsListPage />
              </RequirePermission>
            }
          />
          <Route
            path="/configuracoes/ia/decisoes/conversa/:conversationId"
            element={
              <RequirePermission access={ADMIN_ACCESS.aiDecisions}>
                <ConversationTimelinePage />
              </RequirePermission>
            }
          />
          <Route
            path="/configuracoes/ia/playground"
            element={
              <RequirePermission access={ADMIN_ACCESS.aiPlayground}>
                <PlaygroundPage />
              </RequirePermission>
            }
          />
          {/* F25-S07: painel "IA no funil" — ações autônomas + reversão */}
          <Route
            path="/configuracoes/ia/acoes"
            element={
              <RequirePermission access={ADMIN_ACCESS.aiActions}>
                <AiActionsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/cities"
            element={
              <RequirePermission access={ADMIN_ACCESS.cities}>
                <CitiesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/feature-flags"
            element={
              <RequirePermission access={ADMIN_ACCESS.featureFlags}>
                <FeatureFlagsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/products"
            element={
              <RequirePermission access={ADMIN_ACCESS.products}>
                <ProductsPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/products/:id"
            element={
              <RequirePermission access={ADMIN_ACCESS.products}>
                <ProductDetailPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/users"
            element={
              <RequirePermission access={ADMIN_ACCESS.users}>
                <UsersPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/papeis"
            element={
              <RequirePermission access={ADMIN_ACCESS.roles}>
                <RolesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/agents"
            element={
              <RequirePermission access={ADMIN_ACCESS.agents}>
                <AgentsPage />
              </RequirePermission>
            }
          />
          {/* Canais de mensagem (WhatsApp Business) */}
          <Route
            path="/admin/canais"
            element={
              <RequirePermission access={ADMIN_ACCESS.channels}>
                <CanaisAdminPage />
              </RequirePermission>
            }
          />
          {/* F12-S10: Tutoriais em vídeo */}
          <Route
            path="/admin/tutoriais"
            element={
              <RequirePermission access={ADMIN_ACCESS.tutorials}>
                <TutoriaisPage />
              </RequirePermission>
            }
          />
          {/* F24-S10: Regras de notificação */}
          <Route
            path="/admin/notificacoes"
            element={
              <RequirePermission access={ADMIN_ACCESS.notifications}>
                <NotificacoesPage />
              </RequirePermission>
            }
          />
          {/* F5-S05: Follow-up — réguas e jobs */}
          <Route
            path="/admin/followup/rules"
            element={
              <RequirePermission access={ADMIN_ACCESS.followupRules}>
                <FollowupRulesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/followup/jobs"
            element={
              <RequirePermission access={ADMIN_ACCESS.followupJobs}>
                <FollowupJobsPage />
              </RequirePermission>
            }
          />
          {/* F5-S08: Cobrança — parcelas, réguas, jobs */}
          <Route
            path="/admin/billing/dues"
            element={
              <RequirePermission access={ADMIN_ACCESS.billingDues}>
                <PaymentDuesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/billing/rules"
            element={
              <RequirePermission access={ADMIN_ACCESS.billingRules}>
                <CollectionRulesPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/billing/jobs"
            element={
              <RequirePermission access={ADMIN_ACCESS.billingJobs}>
                <CollectionJobsPage />
              </RequirePermission>
            }
          />
          {/* F5-S09: Templates WhatsApp */}
          <Route
            path="/admin/templates"
            element={
              <RequirePermission access={ADMIN_ACCESS.templates}>
                <TemplatesListPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/templates/new"
            element={
              <RequirePermission access={ADMIN_ACCESS.templatesWrite}>
                <TemplateFormPage />
              </RequirePermission>
            }
          />
          <Route
            path="/admin/templates/:id"
            element={
              <RequirePermission access={ADMIN_ACCESS.templates}>
                <TemplateDetailPage />
              </RequirePermission>
            }
          />
          {/* F28-S07: Respostas rápidas — biblioteca de mensagens do live chat */}
          <Route
            path="/admin/quick-replies"
            element={
              <RequirePermission access={ADMIN_ACCESS.quickReplies}>
                <QuickRepliesPage />
              </RequirePermission>
            }
          />
          {/* F47-S08: atribuição usuário↔canal do live chat (atrás de livechat.inbox_scope.enabled) */}
          <Route
            path="/admin/inbox-assignments"
            element={
              <RequirePermission access={ADMIN_ACCESS.inboxAssignments}>
                <InboxAssignmentsPage />
              </RequirePermission>
            }
          />
          {/* F46-S12: catálogo de tipos de documento (atrás de documents:manage +
              documents.enabled — flag nasce OFF, ver cabeçalho de DocumentTypesPage.tsx) */}
          <Route
            path="/admin/document-types"
            element={
              <RequirePermission access={ADMIN_ACCESS.documentTypes}>
                <DocumentTypesPage />
              </RequirePermission>
            }
          />
          {/* F51-S17: relação de quem confirmou ciência da política de monitoramento
              da linha corporativa, com exportação em CSV. Gate policies:manage é
              reaplicado dentro da própria página (PolicyAcceptancesPage.tsx). Única
              política ativa hoje é a de monitoramento — chave canônica compartilhada
              com o gate que trava /meu-whatsapp (WHATSAPP_MONITORING_POLICY_KEY). */}
          <Route
            path="/admin/policies/acceptances"
            element={
              <RequirePermission access={ADMIN_ACCESS.policies}>
                <PolicyAcceptancesPage
                  policyKey={WHATSAPP_MONITORING_POLICY_KEY}
                  policyTitle="Monitoramento da linha corporativa"
                />
              </RequirePermission>
            }
          />
          {/* F16-S16: Inbox live chat — layout 3 colunas + ChatList */}
          <Route
            path="/conversas"
            element={
              <RequirePermission access={NAV_ACCESS.conversations}>
                <ConversasPage />
              </RequirePermission>
            }
          />
          {/* F15-S10: Painel de tarefas */}
          <Route
            path="/tarefas"
            element={
              <RequirePermission access={NAV_ACCESS.tasks}>
                <TasksPage />
              </RequirePermission>
            }
          />
          {/* F26-S04: Central de notificações (além das 10 do dropdown) */}
          <Route path="/notificacoes" element={<NotificationsPage />} />
          {/* F10-S02: Central de Ajuda */}
          <Route path="/ajuda" element={<HelpHomePage />} />
          {/* F10-S10: API Reference — ANTES do wildcard /ajuda/* */}
          <Route
            path="/ajuda/api/:resource?"
            element={
              <React.Suspense fallback={null}>
                <ApiReferencePage />
              </React.Suspense>
            }
          />
          <Route path="/ajuda/*" element={<DocPage />} />
        </Route>

        {/* ── Catch-all ────────────────────────────────────────────────────── */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </>
  );
}
